import { useId } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import { Hourglass } from "lucide-react";

// The "coming soon" dialog for an action that exists in the design but isn't
// built yet: the broker learns it is planned, and nothing pretends to have
// happened. Shared by the record page's Actions menu and the bond
// submission's action row (BRK-S399), so the two say it the same way.

const COMING_SOON_DEFAULT =
  "We're still building this feature. It will appear here as soon as it's ready.";

export function ComingSoonDialog({
  open,
  onClose,
  label,
  message,
}: {
  open: boolean;
  onClose: () => void;
  /** The action's name: the title reads "{label} is coming soon". */
  label: string;
  /** The body, when the default is too vague. */
  message?: string;
}) {
  const idBase = useId();
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      aria-labelledby={`${idBase}-soon-title`}
      aria-describedby={`${idBase}-soon-body`}
      slotProps={{ paper: { sx: { borderRadius: 4 } } }}
    >
      <DialogContent sx={{ textAlign: "center", pt: 4, px: 3.5 }}>
        <Box
          aria-hidden
          sx={(t) => ({
            width: 52,
            height: 52,
            mx: "auto",
            mb: 2,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: alpha(t.palette.primary.main, 0.1),
            color: "primary.main",
          })}
        >
          <Hourglass size={24} />
        </Box>
        <Typography id={`${idBase}-soon-title`} component="h2" variant="h2">
          {label ? `${label} is coming soon` : ""}
        </Typography>
        <Typography
          id={`${idBase}-soon-body`}
          sx={{ mt: 1, color: "text.secondary" }}
        >
          {message ?? COMING_SOON_DEFAULT}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center", pb: 3.5, pt: 1 }}>
        <Button
          variant="contained"
          onClick={onClose}
          autoFocus
          sx={{ minWidth: 120 }}
        >
          Got it
        </Button>
      </DialogActions>
    </Dialog>
  );
}
